import { HttpClient, HttpParams } from '@angular/common/http';
import type { AccessUsersQuery, AccessUsersPage, ConfigurationAccessSession } from '../../dom/models/configuration.models';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  AccessCatalog,
  AccessPermission,
  AccessPermissionPayload,
  AccessRole,
  AccessRolePayload,
  ApplicationProduct,
  ApplicationUserCount,
  AppMenu,
  AppMenuPayload,
  ConfigurationAccessUser,
  ConfigurationAuthenticatedUser,
  Enterprise,
  GfsysProduct,
  GfsysProductPayload,
  InitializedEnvironment,
  OAuthClient,
  OAuthClientFormOptions,
  OAuthClientPayload,
  TaxAssignment,
  TaxCatalogs,
  TaxPercentage,
  TaxType,
  WolopBranding,
} from '../../dom/models/configuration.models';
import { ConfigurationRepository } from '../../dom/ports/configuration.repository';

interface ApiResponse<T> {
  data: T;
}

@Injectable()
export class ConfigurationHttpRepository implements ConfigurationRepository {
  public getBranding() {
    return this.unwrap(this.http.get<ApiResponse<WolopBranding>>(`${this.baseUrl}/branding`));
  }
  public uploadBrandingLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.unwrap(
      this.http.post<ApiResponse<WolopBranding>>(`${this.baseUrl}/branding/logo`, formData),
    );
  }
  public getApplicationUserCounts() {
    return this.unwrap(this.http.get<ApiResponse<ApplicationUserCount[]>>(`${this.baseUrl}/dashboard/user-counts`));
  }
  public getAccessMe() {
    return this.unwrap(this.http.get<ApiResponse<ConfigurationAccessSession>>(`${this.baseUrl}/access/me`));
  }
  public getAccessDefaultRole() {
    return this.unwrap(this.http.get<ApiResponse<AccessRole | null>>(`${this.baseUrl}/access/default-role`));
  }
  public setAccessDefaultRole(roleId: number) {
    return this.unwrap(this.http.put<ApiResponse<AccessRole>>(`${this.baseUrl}/access/default-role`, { roleId }));
  }
  public removeAccessUser(sourceUserId: number) {
    return this.unwrap(this.http.delete<ApiResponse<null>>(`${this.baseUrl}/access/users/${sourceUserId}`));
  }
  public getAccessUsers(query: AccessUsersQuery) {
    const params = new HttpParams({ fromObject: { ...query } });
    return this.unwrap(this.http.get<ApiResponse<AccessUsersPage>>(`${this.baseUrl}/access/users`, { params }));
  }
  public getAuthenticatedUser() {
    return this.unwrap(
      this.http.get<ApiResponse<ConfigurationAuthenticatedUser>>(this.baseUrl + '/user'),
    );
  }

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/configuration`;

  public getAppMenu() {
    return this.unwrap(this.http.get<ApiResponse<AppMenu[]>>(`${this.baseUrl}/menu`));
  }
  public getAppMenuItem(id: number) {
    return this.unwrap(this.http.get<ApiResponse<AppMenu>>(`${this.baseUrl}/menu/${id}`));
  }
  public createMenu(payload: AppMenuPayload) {
    return this.unwrap(this.http.post<ApiResponse<AppMenu>>(`${this.baseUrl}/menu`, payload));
  }
  public updateMenu(id: number, payload: Partial<AppMenuPayload>) {
    return this.unwrap(
      this.http.patch<ApiResponse<AppMenu>>(`${this.baseUrl}/menu/${id}`, payload),
    );
  }
  public removeMenu(id: number) {
    return this.unwrap(this.http.delete<ApiResponse<null>>(`${this.baseUrl}/menu/${id}`));
  }
  public updateMenuOrder(items: Array<{ id: number; position: number }>) {
    return this.unwrap(
      this.http.put<ApiResponse<AppMenu[]>>(`${this.baseUrl}/menu/order`, { items }),
    );
  }

  public getEnterprises() {
    return this.unwrap(this.http.get<ApiResponse<Enterprise[]>>(`${this.baseUrl}/enterprises`));
  }
  public getApplications(enterpriseId: number) {
    return this.unwrap(
      this.http.get<ApiResponse<ApplicationProduct[]>>(
        `${this.baseUrl}/enterprises/${enterpriseId}/applications`,
      ),
    );
  }
  public updateApplicationStatus(payload: {
    enterpriseId: number;
    productId: number;
    active: boolean;
  }) {
    return this.unwrap(
      this.http.patch<ApiResponse<ApplicationProduct>>(
        `${this.baseUrl}/applications/status`,
        payload,
      ),
    );
  }
  public initializeEnvironment(payload: { enterpriseId: number; productId: number }) {
    return this.unwrap(
      this.http.post<ApiResponse<InitializedEnvironment>>(
        `${this.baseUrl}/applications/initialize-environment`,
        payload,
      ),
    );
  }

  public getProducts() {
    return this.unwrap(this.http.get<ApiResponse<GfsysProduct[]>>(`${this.baseUrl}/products`));
  }
  public getProduct(id: number) {
    return this.unwrap(this.http.get<ApiResponse<GfsysProduct>>(`${this.baseUrl}/products/${id}`));
  }
  public createProduct(payload: GfsysProductPayload) {
    return this.unwrap(
      this.http.post<ApiResponse<GfsysProduct>>(`${this.baseUrl}/products`, payload),
    );
  }
  public updateProduct(id: number, payload: GfsysProductPayload) {
    return this.unwrap(
      this.http.put<ApiResponse<GfsysProduct>>(`${this.baseUrl}/products/${id}`, payload),
    );
  }
  public uploadProductImage(id: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.unwrap(
      this.http.post<ApiResponse<GfsysProduct>>(`${this.baseUrl}/products/${id}/image`, formData),
    );
  }
  public removeProduct(id: number) {
    return this.unwrap(this.http.delete<ApiResponse<null>>(`${this.baseUrl}/products/${id}`));
  }
  public assignProductToEnterprise(payload: {
    productId: number;
    enterpriseId: number;
    active: boolean;
  }) {
    return this.unwrap(
      this.http.post<ApiResponse<ApplicationProduct>>(
        `${this.baseUrl}/products/${payload.productId}/enterprises/${payload.enterpriseId}`,
        { active: payload.active },
      ),
    );
  }
  public removeProductFromEnterprise(payload: { productId: number; enterpriseId: number }) {
    return this.unwrap(
      this.http.delete<ApiResponse<null>>(
        `${this.baseUrl}/products/${payload.productId}/enterprises/${payload.enterpriseId}`,
      ),
    );
  }

  public getOAuthClients() {
    return this.unwrap(this.http.get<ApiResponse<OAuthClient[]>>(`${this.baseUrl}/oauth-clients`));
  }
  public getOAuthClient(id: string) {
    return this.unwrap(
      this.http.get<ApiResponse<OAuthClient>>(`${this.baseUrl}/oauth-clients/${id}`),
    );
  }
  public getOAuthClientFormOptions() {
    return this.unwrap(
      this.http.get<ApiResponse<OAuthClientFormOptions>>(
        `${this.baseUrl}/oauth-clients/form-options`,
      ),
    );
  }
  public createOAuthClient(payload: OAuthClientPayload) {
    return this.unwrap(
      this.http.post<ApiResponse<OAuthClient>>(`${this.baseUrl}/oauth-clients`, payload),
    );
  }
  public updateOAuthClient(id: string, payload: OAuthClientPayload) {
    return this.unwrap(
      this.http.put<ApiResponse<OAuthClient>>(`${this.baseUrl}/oauth-clients/${id}`, payload),
    );
  }
  public removeOAuthClient(id: string) {
    return this.unwrap(this.http.delete<ApiResponse<null>>(`${this.baseUrl}/oauth-clients/${id}`));
  }

  public getAccessRoles(): Observable<AccessRole[]> {
    return this.unwrap(this.http.get<ApiResponse<AccessRole[]>>(`${this.baseUrl}/access/roles`));
  }
  public getAccessPermissions(): Observable<AccessPermission[]> {
    return this.unwrap(this.http.get<ApiResponse<AccessPermission[]>>(`${this.baseUrl}/access/permissions`));
  }
  public getAccessEditorOptions(): Observable<AccessCatalog> {
    return this.unwrap(this.http.get<ApiResponse<AccessCatalog>>(`${this.baseUrl}/access/editor-options`));
  }
  public getAccessCatalog() {
    return this.unwrap(this.http.get<ApiResponse<AccessCatalog>>(`${this.baseUrl}/access/catalog`));
  }
  public getAccessRole(id: number) {
    return this.unwrap(
      this.http.get<ApiResponse<AccessRole>>(`${this.baseUrl}/access/roles/${id}`),
    );
  }
  public getAccessPermission(id: number) {
    return this.unwrap(
      this.http.get<ApiResponse<AccessPermission>>(`${this.baseUrl}/access/permissions/${id}`),
    );
  }
  public getAccessUser(sourceUserId: number) {
    return this.unwrap(
      this.http.get<ApiResponse<ConfigurationAccessUser>>(
        `${this.baseUrl}/access/users/${sourceUserId}`,
      ),
    );
  }
  public createAccessRole(payload: AccessRolePayload) {
    return this.unwrap(
      this.http.post<ApiResponse<AccessRole>>(`${this.baseUrl}/access/roles`, payload),
    );
  }
  public updateAccessRole(id: number, payload: AccessRolePayload) {
    return this.unwrap(
      this.http.put<ApiResponse<AccessRole>>(`${this.baseUrl}/access/roles/${id}`, payload),
    );
  }
  public removeAccessRole(id: number) {
    return this.unwrap(this.http.delete<ApiResponse<null>>(`${this.baseUrl}/access/roles/${id}`));
  }
  public createAccessPermission(payload: AccessPermissionPayload) {
    return this.unwrap(
      this.http.post<ApiResponse<AccessPermission>>(`${this.baseUrl}/access/permissions`, payload),
    );
  }
  public updateAccessPermission(id: number, payload: AccessPermissionPayload) {
    return this.unwrap(
      this.http.put<ApiResponse<AccessPermission>>(
        `${this.baseUrl}/access/permissions/${id}`,
        payload,
      ),
    );
  }
  public removeAccessPermission(id: number) {
    return this.unwrap(
      this.http.delete<ApiResponse<null>>(`${this.baseUrl}/access/permissions/${id}`),
    );
  }
  public importAccessUsers(payload: { userIds: number[]; defaultRoleId?: number }) {
    return this.unwrap(
      this.http.post<ApiResponse<ConfigurationAccessUser[]>>(
        `${this.baseUrl}/access/users/import`,
        { userIds: payload.userIds },
      ),
    );
  }
  public updateAccessUserRoles(sourceUserId: number, roleIds: number[]) {
    return this.unwrap(
      this.http.put<ApiResponse<ConfigurationAccessUser>>(
        `${this.baseUrl}/access/users/${sourceUserId}/roles`,
        { roleIds },
      ),
    );
  }
  public getAccessMenuForUser(sourceUserId: number) {
    return this.unwrap(
      this.http.get<ApiResponse<AppMenu[]>>(`${this.baseUrl}/access/users/${sourceUserId}/menu`),
    );
  }

  public getTaxCatalogs(enterpriseId?: number) {
    const params =
      enterpriseId === undefined
        ? undefined
        : new HttpParams({ fromObject: { enterpriseId: String(enterpriseId) } });
    return this.unwrap(
      this.http.get<ApiResponse<TaxCatalogs>>(`${this.baseUrl}/tax/catalogs`, { params }),
    );
  }
  public getTaxPercentage(id: number) {
    return this.unwrap(
      this.http.get<ApiResponse<TaxPercentage>>(`${this.baseUrl}/tax/percentages/${id}`),
    );
  }
  public getTaxType(id: number) {
    return this.unwrap(this.http.get<ApiResponse<TaxType>>(`${this.baseUrl}/tax/types/${id}`));
  }
  public getTaxAssignment(id: number) {
    return this.unwrap(
      this.http.get<ApiResponse<TaxAssignment>>(`${this.baseUrl}/tax/assignments/${id}`),
    );
  }
  public createTaxPercentage(payload: Omit<TaxPercentage, 'id'>) {
    return this.unwrap(
      this.http.post<ApiResponse<TaxPercentage>>(`${this.baseUrl}/tax/percentages`, payload),
    );
  }
  public updateTaxPercentage(id: number, payload: Partial<Omit<TaxPercentage, 'id'>>) {
    return this.unwrap(
      this.http.patch<ApiResponse<TaxPercentage>>(`${this.baseUrl}/tax/percentages/${id}`, payload),
    );
  }
  public removeTaxPercentage(id: number) {
    return this.unwrap(
      this.http.delete<ApiResponse<null>>(`${this.baseUrl}/tax/percentages/${id}`),
    );
  }
  public createTaxType(payload: Omit<TaxType, 'id'>) {
    return this.unwrap(this.http.post<ApiResponse<TaxType>>(`${this.baseUrl}/tax/types`, payload));
  }
  public updateTaxType(id: number, payload: Partial<Omit<TaxType, 'id'>>) {
    return this.unwrap(
      this.http.patch<ApiResponse<TaxType>>(`${this.baseUrl}/tax/types/${id}`, payload),
    );
  }
  public removeTaxType(id: number) {
    return this.unwrap(this.http.delete<ApiResponse<null>>(`${this.baseUrl}/tax/types/${id}`));
  }
  public createTaxAssignment(payload: Omit<TaxAssignment, 'id'>) {
    return this.unwrap(
      this.http.post<ApiResponse<TaxAssignment>>(`${this.baseUrl}/tax/assignments`, payload),
    );
  }
  public updateTaxAssignment(id: number, payload: Partial<Omit<TaxAssignment, 'id'>>) {
    return this.unwrap(
      this.http.patch<ApiResponse<TaxAssignment>>(`${this.baseUrl}/tax/assignments/${id}`, payload),
    );
  }
  public removeTaxAssignment(id: number) {
    return this.unwrap(
      this.http.delete<ApiResponse<null>>(`${this.baseUrl}/tax/assignments/${id}`),
    );
  }

  private unwrap<T>(request: Observable<ApiResponse<T>>): Observable<T> {
    return request.pipe(map((response) => response.data));
  }
}
